<template>
  <Card :header="title">
    <template #header-actions>
      <div class="d-flex">
        <div class="icons-wrapper">
          <span class="icon-class" @click="handleZoomOut">
            <BaseIcon name="zoomOut" width="2.438em" height="2.438em" />
          </span>
          <span class="icon-class" @click="handleZoomIn">
            <BaseIcon name="zoomIn" width="2.438em" height="2.438em" />
          </span>
          <span class="icon-class" @click="handleReset">
            <BaseIcon
              :name="secondaryResetIcon ? 'resetSecondary' : 'resetOpen'"
              width="1.438em"
              height="1.438em"
            />
          </span>

          <RexMoreOptionAction
            :options="moreOptionsData"
            :data="getMoreOptionsData"
            v-if="showMoreOptions"
          />
        </div>
      </div>
    </template>
    <template #card-body>
      <div class="graph-container">
        <Loading v-if="loading" />
        <div v-if="!loading">
          <div class="graph">
            <apexchart
              ref="chart"
              height="100%"
              :options="updateOptions"
              :series="series"
            ></apexchart>
          </div>
          <div class="legend">
            <div class="table-haeder">
              <span style="visibility: hidden" class="dot"></span>
              <div
                v-for="(header, index) in legendHeaders"
                :key="index"
                class="header-heading"
                :style="{ width: header.width, 'text-align': 'left' }"
              >
                <RexTableSortField
                  v-if="header.sortable && !disableSort"
                  :name="header.label"
                  @toggleSort="toggleSorting(header)"
                  :isAsc="isAsc"
                  :isActiveField="sortingField === header.key"
                />
                <span v-else>
                  {{ header.label }}
                </span>
              </div>
            </div>
            <div
              class="table-body"
              :style="{
                'justify-content':
                  sortedData.length === 0 ? 'center' : 'flex-start',
              }"
            >
              <div v-if="sortedData.length === 0" class="no-data">
                {{ $t('noData') }}
              </div>
              <div
                v-for="(row, index) in sortedData"
                class="table-row"
                :key="index"
                @click="(e) => selectRow(row.label, false)"
              >
                <span
                  class="dot"
                  :style="{
                    background: row?.label.includes('Previous')
                      ? 'none'
                      : colors[index],
                    border: row?.label.includes('Previous')
                      ? `2px dashed ${colors[index]}`
                      : 'none',
                  }"
                ></span>
                <div
                  v-for="(header, index) in legendHeaders"
                  :key="index"
                  :class="highLightRow(row)"
                  :style="{
                    width: header.width,
                    'text-align': 'left',
                  }"
                >
                  {{ row[header.key] }}
                  <span @click="(e) => redirectToApm(e, row)">
                    <BaseIcon
                      v-if="header.redirect"
                      name="redirectTimeSM"
                      class="apdex-app-list-redirect-icon"
                      width="1.2rem"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>
<script>
/**
 * @author : Tarun Rawat
 * This is multi line chart. It contains table as legend.
 *
 */
import {
  getColor,
  handleZoomOut,
  _getNewMinXMaxX,
  handleReset,
  updateMinMax,
  handleZoomIn,
  colors,
} from './util';
import Card from '@/rex-components/rex-card.vue';
import BaseIcon from '@/rex-components/BaseIcon.vue';
import { replaceSpaceInId, sortWithDataType } from '@/utils/helper-function';
import {
  XAxisConfig,
  YAxisConfig,
  Grid,
  MultiLineToolTip,
  ChartToolBar,
  lineWidth,
  dashLinesize,
  tooltipValues,
  getCustomTooltipContent,
} from './charts.global.config';
import Loading from '@/rex-components/rex-loading.vue';
import RexTableSortField from '@/rex-components/rex-TableSortField.vue';
import RexMoreOptionAction from '@/rex-components/RexMoreOptionAction.vue';
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  components: {
    Card,
    BaseIcon,
    Loading,
    RexTableSortField,
    RexMoreOptionAction,
  },
  emits: ['handleMoreOptionsClick', 'selectedRow', 'openModal', 'closeModal'],
  data() {
    return {
      series: [],
      chartConfig: {
        chartOptions: {
          colors: [...colors],
          chart: {
            height: '100%',
            width: '100%',
            type: 'line',
            zoom: {
              enabled: true,
              type: 'x',
            },
            group: this.isSynchronized ? 'group' : '',
            toolbar: {
              show: false,
            },
            id: this.chartId,
            redrawOnParentResize: true,
            events: {
              zoomed: (chartContext, { xaxis, yaxis }) => {
                this.updateMinMax(xaxis);
              },
              mounted: (chart) => {
                chart.windowResizeHandler();
                //If there was row which was selected already and when
                // new data comes in,persists the selected row
                if (this.selectedRow) {
                  this.selectRow(this.selectedRow, true);
                }
              },
              toolbar: ChartToolBar,
            },
            animations: {
              enabled: this.animations,
            },
          },
          tooltip: MultiLineToolTip,
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'straight',
            width: 2,
          },
          grid: Grid,
          xaxis: {
            labels: XAxisConfig.labels,
            tickAmount: this.intervalTime.length > 14 ? 5 : 3,
            min: this.intervalTime[0],
            title: {
              ...XAxisConfig.title,
              text: this.Graphlabels.x,
            },
            tooltip: {
              enabled: false,
            },
          },
          yaxis: {
            ...YAxisConfig,
            labels: YAxisConfig.labels,
            title: {
              ...YAxisConfig.title,
              text: this.Graphlabels.y,
            },
          },
          legend: {
            show: false,
          },
          markers: {
            size: 0,
          },
        },
      },
      colors: [],
      selectedRow: '',
      minX: 1,
      maxX: this.intervalTime.length,
      isAsc: false,
      sortingField: 'avg',
      dataType: 'number',
      sortedData: [],
    };
  },
  props: {
    data: { type: Array, default: () => [] },
    moreOptionsData: { type: Array, default: () => [] },
    showLegend: { default: false },
    legendHeaders: { default: () => [], type: Array },
    highLightColValue: { default: '', required: false },
    intervalTime: {
      type: Array,
    },
    chartId: { type: String, required: true },
    //insideModal is set true when graph is inside modal
    insideModal: { type: Boolean, default: false },
    title: { type: String, required: true },
    modalNeeded: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    graphIndex: { type: Number, default: 0 },
    formula: { type: Function, default: () => {} },
    secondaryResetIcon: {
      type: Boolean,
      default: false,
    },
    isSynchronized: {
      type: Boolean,
      default: false,
    },
    Graphlabels: {
      type: Object,
      default: () => {
        return { x: 'Time', y: 'Value' };
      },
    },
    animations: { type: Boolean, default: false },
    showMoreOptions: { type: Boolean, default: false },
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    /**
     * Whenever data is updated, update the graph
     */
    data: {
      handler: function (newVal) {
        this.sortedData = this.isCompareOptionsSelected
          ? newVal
          : sortWithDataType(
              newVal,
              this.sortingField,
              this.dataType,
              this.isAsc,
            );
        this.updateGraph(this.sortedData);
      },
      deep: true,
    },
    Graphlabels: {
      deep: true,
      handler(_newValue) {
        this.chartConfig.chartOptions.yaxis.title.text = _newValue.y;
        this.$refs?.chart?.value?.refresh?.();
        if (this.insideModal && this.$refs?.chart) {
          this.$refs.chart.updateOptions({
            yaxis: {
              ...YAxisConfig,
              labels: YAxisConfig.labels,
              title: { ...YAxisConfig.title, text: _newValue.y },
            },
          });
        }
      },
    },
    getDurationTime: function () {
      this.isAsc = false;
      this.sortingField = 'avg';
    },
    isCompareOptionsSelected: function (newVal) {
      if (newVal) {
        this.updateGraph(this.data);
      }
    },
  },
  methods: {
    /**
     * Some methods are defined in some utils file so that it can reused some where else
     */
    ...mapActions('mobileState/mobileCommonState', ['selectMobileService']),
    getColor,
    replaceSpaceInId,
    async updateGraph(newValues) {
      let arr = [];
      let colors = [];
      this.selectedRow = '';
      let updatedColors = [];
      /**
       * Marking x and y values
       */
      try {
        Array.isArray(newValues) &&
          newValues.forEach((val, index) => {
            let max = 0;
            let avg = 0;
            arr.push({
              name: val.label,

              data: val.values.values?.map((xVal, index) => {
                max = Math.max(max, xVal.value);
                avg += xVal.value;
                return {
                  x:
                    typeof this.intervalTime[index] === 'undefined'
                      ? ''
                      : this.intervalTime[index],
                  y: this.formula(xVal.value),
                };
              }),
            });
            let total = val.values
              ? val.values.values
                ? val.values.values.length
                : 0
              : 0;
            if (val.avg === undefined) {
              val.avg = avg / total;
              val.avg = this.formula(val.avg);
            } else if (!val.addedAvgFormula) {
              val.avg = this.formula(val.avg);
              val.addedAvgFormula = true;
            }

            if (val.max === undefined) {
              val.max = max;
              val.max = this.formula(val.max);
              val.addedMaxFormula = true;
            } else if (!val.addedMaxFormula) {
              val.max = this.formula(val.max);
              val.addedMaxFormula = true;
            }
            if (this.isCompareSelected) {
              updatedColors = colors.flatMap((color) => [color, color]);
              updatedColors.push(getColor(index));
            }
            colors.push(getColor(index));
          });
        this.series = arr;
        if (this.isCompareSelected) {
          this.colors = updatedColors;
        } else this.colors = colors;
        // this.chartConfig.chartOptions.colors = colors;

        /**
         * VERY IMP:
         *
         *
         * Whomover work on creating new graph and get stuck where it wants to
         * update the series
         * If might get this error
         * https://github.com/apexcharts/vue-apexcharts/issues/293
         *
         * I stumbled upon the solution after struggling for hours
         * Please call this method : this.$refs.chart.refresh() after updating series
         *
         *
         *
         */
        // if (this.$refs.chart.value) this.$refs.chart.value.refresh();
      } catch (err) {
        console.log(err);
      }
    },
    selectRow(label, ignoreDoubleClick = false) {
      const { series } = this.$refs.chart;
      //if you select row double times,unselect it
      //persist state after new data comes in
      if (!ignoreDoubleClick && label === this.selectedRow) {
        this.selectedRow = '';
        this.showAllSeries();
        this.$emit('selectedRow', '');
        return;
      }
      let matchFound = false;

      let index = 0;

      while (index < series.length) {
        if (series[index].name == label) {
          matchFound = true;
          break;
        }
        index++;
      }
      /**
       * If match not found, return
       */
      if (!matchFound) return;
      /**
       * Imp:
       * There is bug in apexchart.
       * There was performance issue while  iterating over series and hiding/showing it
       * them. It was freezing the screen if no of series are more.
       * Issue is desc here : https://github.com/apexcharts/apexcharts.js/issues/1787
       * Therefore applying a fix to it
       *
       */
      const update = this.$refs.chart.chart.update; //taking update function
      this.$refs.chart.chart.update = () => Promise.resolve(); //replacing it fake dummy fn
      /**
       * Match found would be false,if a row is selected and someone changes the cluster
       */

      series.forEach((sample) => {
        if (sample.name != label) this.$refs.chart.hideSeries(sample.name);
      });
      //if match is found, update the series
      this.$refs.chart.showSeries(label);

      this.$refs.chart.chart.update = update; //reassiging it
      this.$refs.chart.chart.update?.(); //calling it manually
      this.selectedRow = label;
      this.$emit('selectedRow', label);
    },
    toggleSorting(sortingField) {
      this.isAsc = !this.isAsc;
      this.sortingField = sortingField.key;
      this.dataType = sortingField?.dataType ?? 'string';
      this.sortedData = sortWithDataType(
        this.data,
        this.sortingField,
        this.dataType,
        this.isAsc,
      );
      this.updateGraph(this.sortedData);
    },
    showAllSeries() {
      const { series } = this.$refs.chart;
      const update = this.$refs.chart.chart.update;
      this.$refs.chart.chart.update = () => Promise.resolve();

      series.forEach((sample) => this.$refs.chart.showSeries(sample.name));

      this.$refs.chart.chart.update = update;
      this.$refs.chart.chart.update();
    },
    highLightRow(row) {
      return row.label === this.selectedRow
        ? 'highlight overflow-row'
        : 'overflow-row';
    },
    /**
     * Just using some reusing of methods
     * This methods are defined somewheere and can be used is same way in another graph component
     */
    handleZoomOut,
    _getNewMinXMaxX,
    handleReset() {
      //If modal is not needed then just call handleReset Method with binded this
      if (!this.modalNeeded) {
        handleReset.call(this);
      } else {
        if (!this.insideModal) this.$emit('openModal', this.graphIndex);
        else this.$emit('closeModal', this.graphIndex);
      }
    },
    handleZoomIn,
    updateMinMax,
    redirectToApm(e, header) {
      e.stopPropagation();
      this.$router.push(header.redirect);
    },
  },
  computed: {
    ...mapGetters('globalState/settings', [
      'getDurationTime',
      'compareIntervalTime',
    ]),
    ...mapState('globalState/settings', ['isCompareOptionsSelected']),
    ...mapState('globalState/app', [
      'currentCapability',
      'compareSupportedCapabilities',
      'currentSideNav',
    ]),
    getMoreOptionsData() {
      return {
        series: this.series,
        title: this.title,
      };
    },
    isCompareSelected() {
      return (
        this.isCompareOptionsSelected &&
        this.compareSupportedCapabilities.includes(this.currentSideNav.key)
      );
    },
    getSeriesTooltip() {
      if (
        !this.compareSupportedCapabilities.includes(this.currentSideNav.key)
      ) {
        return tooltipValues;
      } else {
        return {
          ...tooltipValues,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            return getCustomTooltipContent(
              series,
              this.data,
              this.intervalTime,
              this.compareIntervalTime,
              this.item,
              dataPointIndex,
              w,
              this.currentCapability,
              this.title,
            );
          },
        };
      }
    },
    updateOptions() {
      const dashArray = [];
      const widthArray = [];
      const duplicatedColors = colors.flatMap((color) => [color, color]);
      this.data.forEach((i) => {
        const isCompared = i.label.includes('Previous');
        dashArray.push(isCompared ? dashLinesize : 0);
        widthArray.push(lineWidth);
      });
      const updatedTemp = {
        ...this.chartConfig.chartOptions,
        stroke: {
          dashArray,
          width: widthArray,
        },
        tooltip: this.isCompareSelected
          ? this.getSeriesTooltip
          : MultiLineToolTip,
        colors: this.isCompareSelected ? duplicatedColors : colors,
        xaxis: {
          ...this.chartConfig.chartOptions.xaxis,
          min: this.intervalTime[0],
          tickAmount: this.intervalTime.length > 14 ? 5 : 3,
        },
      };
      return updatedTemp;
    },
    disableSort() {
      return (
        this.isCompareOptionsSelected &&
        this.compareSupportedCapabilities.includes(this.currentSideNav.key)
      );
    },
  },
  created() {
    this.updateGraph(this.data);
    this.sortedData = this.data;
  },
  mounted() {
    this.$refs?.chart?.value?.refresh?.();
  },
};
</script>
<style scoped lang="scss">
.graph-container {
  background: #1e2021;
  height: 33rem;
  display: flex;
  flex-direction: column;
}
.graph {
  height: 20rem;
}
.table-haeder {
  display: flex;
  width: 100%;
}
.legend {
  display: flex;
  flex-direction: column;
  padding: 1rem;
}
.table-haeder {
  margin-bottom: 1rem;
}
.table-body {
  display: flex;
  flex-direction: column;
  height: 8rem;
  overflow-y: scroll;
}
.table-row {
  display: flex;
  margin-bottom: 0.5rem;
  cursor: pointer;
}
.header-heading {
  font-weight: bold;
}
.dot {
  height: 10px;
  width: 10px;
  background-color: #bbb;
  border-radius: 50%;
  display: inline-block;
  margin-right: 0.5rem;
  margin-top: 0.4rem;
}
.highlight {
  background: rgba(173, 106, 0, 0.8) !important;
}
.graph-header {
  display: flex;
}
.icon-margin {
  margin-right: 0.5em;
}

.icon-class:hover {
  cursor: pointer;
  svg {
    fill: $font-secondary !important;
  }
}
.icon-class:last-child {
  margin-left: 0.3125em;
}
.icon-class:first-child {
  margin-right: -0.3125em;
}
.overflow-row {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.no-data {
  align-self: center;
}

.icons-wrapper {
  @include flex-center;
}

::v-deep .table-actions-list {
  right: 20px;
}

.apdex-app-list-redirect-icon :deep(svg) {
  height: auto;
  margin-left: 1rem;
}
:deep(.tooltip-marker) {
  margin-right: 5px;
  display: inline-block;
  height: 10px;
  width: 10px;
  border-width: 0px;
  border-radius: 50%;
}
:deep(.series-tooltip-font-group) {
  font-size: 12px;
}
:deep(.series-tooltip-group) {
  font-size: 12px;
  margin-left: 4px;
}
:deep(.legend-dashed-circle) {
  display: inline-block;
  height: 10px;
  width: 10px;
  border-radius: 50%;
  border: 2px dashed currentColor;
  background-color: transparent;
}
:deep(.tooltip-delta) {
  margin-left: 0.5rem;
}
:deep(.custom-title) {
  font-size: 12px;
  text-align: center;
  background: black;
  padding: 0.6rem;
}
:deep(.tooltip-text) {
  font-size: 12px;
  text-align: left;
  padding: 4px;
  margin-right: auto;
}
:deep(.tooltip-series-group) {
  display: flex;
  align-items: center;
}
</style>
